import { CheckIcon } from "lucide-react";
import { cn } from "@devhop/ui";
import type { EventFeaturesDTO } from "../../domain/contracts/event-features";
import { eventHasFeature } from "../../domain/services/event-features";
import {
  isEventDetailTabAvailable,
  type EventDetailTabId,
} from "../lib/event-detail-ui";

export type EventDetailWorkflowStatus = {
  team: boolean;
  pricing: boolean;
  payout: boolean;
  staff: boolean;
};

type EventDetailStepperProps = {
  activeTab: EventDetailTabId;
  onTabClick: (tab: EventDetailTabId) => void;
  workflow: EventDetailWorkflowStatus;
  teamCount: number;
  staffCount?: number;
  eventFeatures: EventFeaturesDTO;
};

const ALL_TABS: {
  tab: EventDetailTabId;
  label: string;
  workflowKey?: keyof EventDetailWorkflowStatus;
  featureKey?: keyof EventFeaturesDTO;
}[] = [
  { tab: "overview", label: "ພາບລວມ" },
  {
    tab: "team",
    label: "ຈັດທີມ",
    workflowKey: "team",
    featureKey: "performers",
  },
  {
    tab: "staff",
    label: "Staff",
    workflowKey: "staff",
    featureKey: "staff",
  },
  {
    tab: "pricing",
    label: "ລາຄາ",
    workflowKey: "pricing",
    featureKey: "documents",
  },
  {
    tab: "payout",
    label: "ເບີກຈ່າຍ",
    workflowKey: "payout",
  },
  {
    tab: "reports",
    label: "ກຳໄລ",
  },
];

const PERFORMER_WORKFLOW_STEP_NUMBER: Partial<Record<EventDetailTabId, number>> =
  {
    team: 1,
    pricing: 2,
    payout: 3,
  };

const STAFF_WORKFLOW_STEP_NUMBER: Partial<Record<EventDetailTabId, number>> = {
  staff: 1,
  pricing: 2,
  payout: 3,
};

export function EventDetailStepper({
  activeTab,
  onTabClick,
  workflow,
  teamCount,
  staffCount = 0,
  eventFeatures,
}: EventDetailStepperProps) {
  const hasPerformers = eventHasFeature(eventFeatures, "performers");
  const hasStaff = eventHasFeature(eventFeatures, "staff");
  const hasCommissions = eventHasFeature(eventFeatures, "commissions");
  const hasProductionCosts = eventHasFeature(eventFeatures, "production_costs");
  const hasOutsources = eventHasFeature(eventFeatures, "outsources");
  const staffPrimary = hasStaff && !hasPerformers;

  const tabs = ALL_TABS.filter((step) => {
    if (step.featureKey) {
      return eventHasFeature(eventFeatures, step.featureKey);
    }
    if (step.tab === "payout") {
      return (
        hasPerformers ||
        hasStaff ||
        hasCommissions ||
        hasProductionCosts ||
        hasOutsources
      );
    }
    if (step.tab === "reports") {
      return isEventDetailTabAvailable("reports", eventFeatures);
    }
    return true;
  });

  const stepNumbers = staffPrimary
    ? STAFF_WORKFLOW_STEP_NUMBER
    : PERFORMER_WORKFLOW_STEP_NUMBER;

  return (
    <nav
      aria-label={staffPrimary ? "ຂັ້ນຕອນງານ Event" : "ຂັ້ນຕອນງານສະແດງ"}
      className="sticky top-0 z-20 -mx-4 mb-6 border-b bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80"
    >
      <div role="tablist" className="flex flex-wrap items-center gap-1 sm:gap-2">
        {tabs.map((step, index) => {
          const isActive = activeTab === step.tab;
          const stepNumber = stepNumbers[step.tab];
          const isComplete =
            step.workflowKey != null && workflow[step.workflowKey];
          const showConnector = index > 0;

          return (
            <div key={step.tab} className="flex items-center gap-1 sm:gap-2">
              {showConnector ? (
                <span
                  className="hidden h-px w-3 bg-border sm:block sm:w-6"
                  aria-hidden
                />
              ) : null}
              <button
                type="button"
                role="tab"
                id={`event-detail-tab-${step.tab}`}
                aria-controls={`event-detail-panel-${step.tab}`}
                aria-selected={isActive}
                onClick={() => onTabClick(step.tab)}
                className={cn(
                  "flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left text-sm transition-colors sm:px-3",
                  isActive
                    ? "border-primary bg-primary/5 font-medium text-foreground"
                    : "border-transparent text-muted-foreground hover:border-border hover:bg-muted/40 hover:text-foreground",
                )}
              >
                {stepNumber != null ? (
                  <span
                    className={cn(
                      "flex size-6 shrink-0 items-center justify-center rounded-full text-xs",
                      isComplete
                        ? "bg-primary text-primary-foreground"
                        : isActive
                          ? "border-2 border-primary bg-background font-semibold text-primary"
                          : "border border-border bg-muted/50 font-medium",
                    )}
                  >
                    {isComplete ? (
                      <CheckIcon className="size-3.5" aria-hidden />
                    ) : (
                      stepNumber
                    )}
                  </span>
                ) : null}
                <span className="whitespace-nowrap">{step.label}</span>
                {step.tab === "team" && teamCount > 0 ? (
                  <span className="rounded-md bg-muted px-1.5 py-0.5 font-medium text-muted-foreground text-xs tabular-nums">
                    {teamCount}
                  </span>
                ) : null}
                {step.tab === "staff" && staffCount > 0 ? (
                  <span className="rounded-md bg-muted px-1.5 py-0.5 font-medium text-muted-foreground text-xs tabular-nums">
                    {staffCount}
                  </span>
                ) : null}
              </button>
            </div>
          );
        })}
      </div>
    </nav>
  );
}
